import React, { useState, useCallback, useEffect } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import useRouter from '../hooks/useRouter'
import useAccount from '../hooks/useAccount'
import useBatchImport from '../hooks/useBatchImport'
import { useFrameworkLibrary, AutoCoderFrameworkLibrary } from '../hooks/useFrameworkLibrary'
import LabelEditor from '../components/autocoder/pipeline/LabelEditor'
import { useConfig } from '../hooks/useConfig'
import { useTeams } from '../hooks/useAdmin'
import { billingTeamRequired, effectiveBillingTeam } from './autocoderCreatePayload'
import { ConfigEntity, ConfigType } from '../types/config'

const steps = ['Upload File', 'Map Columns', 'Select Options', 'Review & Import']

const BatchImportPage: React.FC = () => {
  const router = useRouter()
  const account = useAccount()
  const customer = router.params.customer || account.customer?.name || ''
  const job = router.params.job || account.job?.name || ''

  const [activeStep, setActiveStep] = useState(0)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [selectedFrameworkIds, setSelectedFrameworkIds] = useState<string[]>([])
  const [additionalLabels, setAdditionalLabels] = useState<Record<string, string>>({})
  const [urlColumn, setUrlColumn] = useState<string>('')
  const [labelColumns, setLabelColumns] = useState<Record<string, boolean>>({})
  const [carbonColumns, setCarbonColumns] = useState<Record<string, boolean>>({})
  // The team every pipeline this upload creates is billed to (per-pipeline
  // billing team): required when the customer has an open team, pre-selected
  // when there is one, as in the create wizard.
  const [chosenBillingTeam, setChosenBillingTeam] = useState('')
  const { teams, teamsLoading } = useTeams(customer)
  const openTeams = teams.filter((t) => t.archived_at == null)
  const billingTeamId = effectiveBillingTeam(teams, chosenBillingTeam)
  const needsBillingTeam = billingTeamRequired(teams)

  const {
    mergedConfig,
    configEntity,
    openConfigEntity,
    handleSaveConfig,
  } = useConfig(customer, job)

  // Eagerly load job config on mount so mergedConfig is ready for carbon promotion on import
  useEffect(() => {
    if (customer && job && !configEntity) {
      const jobEntity: ConfigEntity = {
        type: ConfigType.Job,
        customer,
        job,
        display_name: job,
      }
      openConfigEntity(jobEntity)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, job])

  const { analyzeFile, analyzeLoading, analyzeData, createBatchImport, createLoading } = useBatchImport({
    customer,
    job,
  })

  const { frameworks, frameworksLoading } = useFrameworkLibrary({ customer, job })

  const acceptFile = async (file: File) => {
    const lower = file.name.toLowerCase()
    if (!lower.endsWith('.csv') && !lower.endsWith('.xlsx')) return
    setCsvFile(file)
    const result = await analyzeFile(file)
    setUrlColumn(result.detected_url_column || '')
    const labels: Record<string, boolean> = {}
    result.columns.forEach((col) => {
      if (col.name !== result.detected_url_column) labels[col.name] = true
    })
    setLabelColumns(labels)
    setCarbonColumns({})
  }

  // Handle file drop
  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) {
      await acceptFile(file)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyzeFile])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
  }, [])

  // Handle file select
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      await acceptFile(file)
    }
  }

  // Handle import
  const handleImport = async () => {
    if (!csvFile || !urlColumn) return
    if (selectedFrameworkIds.length === 0) return

    try {
      await createBatchImport({
        file: csvFile,
        urlColumn,
        labelColumns: Object.keys(labelColumns).filter((k) => labelColumns[k]),
        frameworkIds: selectedFrameworkIds,
        labels: Object.keys(additionalLabels).length > 0 ? additionalLabels : undefined,
        billingTeamId,
      })
      const carbonKeys = analyzeData
        ? analyzeData.columns.filter((c) => carbonColumns[c.name]).map((c) => c.normalized_key)
        : []
      if (carbonKeys.length > 0 && mergedConfig) {
        const existing = mergedConfig.data.autocoder_carbon_labels || []
        const merged = Array.from(new Set([...existing, ...carbonKeys]))
        await handleSaveConfig({ ...mergedConfig.data, autocoder_carbon_labels: merged })
      }
      router.navigate('autocoder', { customer, job })
    } catch {
      /* handled by hook */
    }
  }

  const handleBack = () => {
    if (activeStep === 0) {
      router.navigate('autocoder', { customer, job })
    } else {
      setActiveStep((prev) => prev - 1)
    }
  }

  const handleNext = () => {
    setActiveStep((prev) => prev + 1)
  }

  const canProceedStep0 = Boolean(csvFile && analyzeData)
  const canProceedStep1 = Boolean(urlColumn)
  const canProceedStep2 =
    selectedFrameworkIds.length > 0 && !teamsLoading && (!needsBillingTeam || billingTeamId !== '')
  const canImport = canProceedStep0 && canProceedStep1 && canProceedStep2

  const getNextDisabled = () => {
    if (activeStep === 0) return !canProceedStep0
    if (activeStep === 1) return !canProceedStep1
    if (activeStep === 2) return !canProceedStep2
    return false
  }

  const renderUploadStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Card
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        sx={{
          border: '2px dashed',
          borderColor: csvFile ? 'success.main' : 'grey.400',
          bgcolor: csvFile ? 'success.light' : 'grey.50',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'primary.light',
          },
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <input
            type="file"
            accept=".csv,.xlsx"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            id="csv-upload"
          />
          <label htmlFor="csv-upload" style={{ cursor: 'pointer', display: 'block' }}>
            {csvFile ? (
              <>
                <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {csvFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {analyzeData ? `Analyzed ${analyzeData.total_rows} rows` : 'Analyzing…'}
                </Typography>
              </>
            ) : (
              <>
                <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.500', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Drop CSV or Excel file here or click to upload
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  One column with content URLs (TikTok, Instagram, or YouTube), plus any label columns
                </Typography>
              </>
            )}
          </label>
        </CardContent>
      </Card>

      {analyzeLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {analyzeData && Object.keys(analyzeData.platform_breakdown || {}).length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="subtitle2">Detected:</Typography>
          {Object.entries(analyzeData.platform_breakdown).map(([p, n]) => (
            <Chip key={p} label={`${p}: ${n}`} color="info" variant="outlined" />
          ))}
        </Box>
      )}
    </Box>
  )

  const renderMapColumnsStep = () => {
    if (!analyzeData) return null
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <FormControl fullWidth size="small">
          <InputLabel>URL Column</InputLabel>
          <Select value={urlColumn} label="URL Column" onChange={(e) => setUrlColumn(e.target.value)}>
            {analyzeData.columns.map((c) => (
              <MenuItem key={c.name} value={c.name}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="subtitle2">Detected:</Typography>
          {Object.entries(analyzeData.platform_breakdown || {}).map(([p, n]) => (
            <Chip key={p} label={`${p}: ${n}`} color="info" variant="outlined" />
          ))}
        </Box>

        {analyzeData.unrecognized_count > 0 && (
          <Alert severity="warning">
            {analyzeData.unrecognized_count} URL(s) were not recognised and will be skipped.
            You can cancel and fix the file, or proceed with the recognised URLs.
          </Alert>
        )}

        <Paper variant="outlined">
          <Typography variant="subtitle2" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            Columns
          </Typography>
          <List dense>
            {analyzeData.columns.filter((c) => c.name !== urlColumn).map((c) => (
              <ListItem
                key={c.name}
                secondaryAction={
                  <Stack direction="row" spacing={2} alignItems="center">
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={!!labelColumns[c.name]}
                          onChange={(e) => setLabelColumns((p) => ({ ...p, [c.name]: e.target.checked }))}
                        />
                      }
                      label="Label"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={!!carbonColumns[c.name]}
                          onChange={(e) => setCarbonColumns((p) => ({ ...p, [c.name]: e.target.checked }))}
                        />
                      }
                      label="Carbon variable"
                    />
                  </Stack>
                }
              >
                <ListItemText
                  primary={c.name}
                  secondary={(c.sample_values || []).slice(0, 2).join(', ')}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Box>
    )
  }

  const renderFrameworkStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Paper variant="outlined">
        <Typography variant="subtitle2" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          Select Frameworks ({selectedFrameworkIds.length} selected)
        </Typography>
        {frameworksLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!frameworksLoading && frameworks.length === 0 && (
          <Alert severity="warning" sx={{ m: 2 }}>
            No frameworks found. Please create a framework first in the Framework Library.
          </Alert>
        )}
        {!frameworksLoading && frameworks.length > 0 && (
          <List disablePadding sx={{ maxHeight: 300, overflow: 'auto' }}>
            {frameworks.map((fw: AutoCoderFrameworkLibrary) => {
              const isSelected = selectedFrameworkIds.includes(fw.id)
              return (
                <ListItemButton
                  key={fw.id}
                  onClick={() => {
                    if (isSelected) {
                      setSelectedFrameworkIds((prev) => prev.filter((id) => id !== fw.id))
                    } else {
                      setSelectedFrameworkIds((prev) => [...prev, fw.id])
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox checked={isSelected} size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <span>{fw.name}</span>
                        {isSelected && <Chip label="Added" size="small" color="success" variant="outlined" />}
                      </Stack>
                    }
                    secondary={`${fw.data?.main_variable?.codes?.length || 0} codes`}
                  />
                </ListItemButton>
              )
            })}
          </List>
        )}
      </Paper>

      <LabelEditor
        labels={additionalLabels}
        onChange={setAdditionalLabels}
      />
    </Box>
  )

  // F1: the coded / scrape-only choice is gone with the pipeline mode it set.
  // Every bulk-upload pipeline is coded; a scrape without coding is now an
  // import receipt with no pipeline, which this wizard does not create.
  const renderOptionsStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {needsBillingTeam && (
        <TextField
          select
          label="Billing team"
          size="small"
          required
          value={billingTeamId}
          onChange={(e) => setChosenBillingTeam(e.target.value)}
          helperText="The team whose AI budget pays for every pipeline this upload creates"
          sx={{ maxWidth: 360 }}
          data-testid="batch-import-billing-team"
        >
          {openTeams.map((t) => (
            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
          ))}
        </TextField>
      )}
      {renderFrameworkStep()}
    </Box>
  )

  const renderReviewStep = () => {
    const selectedFrameworks = frameworks.filter((f: AutoCoderFrameworkLibrary) =>
      selectedFrameworkIds.includes(f.id)
    )
    const totalCodes = selectedFrameworks.reduce(
      (sum, fw) => sum + (fw.data?.main_variable?.codes?.length || 0),
      0
    )
    const totalRows = analyzeData
      ? Object.values(analyzeData.platform_breakdown || {}).reduce((sum, n) => sum + n, 0)
      : 0

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Import Summary
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  File
                </Typography>
                <Typography>{csvFile?.name}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Pipelines to Create
                </Typography>
                <Typography>{totalRows}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Platforms
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {Object.entries(analyzeData?.platform_breakdown || {}).map(([p, n]) => (
                    <Chip key={p} label={`${p}: ${n}`} size="small" color="info" variant="outlined" />
                  ))}
                </Box>
              </Box>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography variant="caption" color="text.secondary">
                  Coding Frameworks
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {selectedFrameworks.length > 0 ? (
                    selectedFrameworks.map((fw) => (
                      <Chip key={fw.id} label={fw.name} size="small" />
                    ))
                  ) : (
                    <Typography>Not selected</Typography>
                  )}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total Codes
                </Typography>
                <Typography>{totalCodes}</Typography>
              </Box>
              {needsBillingTeam && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Billing team
                  </Typography>
                  <Typography>{openTeams.find((t) => t.id === billingTeamId)?.name ?? 'Not selected'}</Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        <Alert severity="info">
          After import, comments will be scraped for each URL, then coding starts automatically.
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: 'calc(100vh - 84px)', p: 3 }}>
      <Paper elevation={1} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Back
          </Button>
          <Typography variant="h5">Batch Import Pipelines</Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: 300 }}>
          {activeStep === 0 && renderUploadStep()}
          {activeStep === 1 && renderMapColumnsStep()}
          {activeStep === 2 && renderOptionsStep()}
          {activeStep === 3 && renderReviewStep()}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          {activeStep < 3 ? (
            <Button
              variant="contained"
              color="info"
              onClick={handleNext}
              disabled={getNextDisabled()}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="info"
              startIcon={createLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleImport}
              disabled={!canImport || createLoading}
            >
              {createLoading ? 'Importing…' : `Import ${Object.values(analyzeData?.platform_breakdown || {}).reduce((s, n) => s + n, 0)} Pipelines`}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  )
}

export default BatchImportPage
